import { useFFmpeg } from "app/hooks";
import { errorService } from "app/utils";
import KEYS from "../constants";
import { HandleMediaChange, PostStoryLocals, PostStoryState, SetState } from "../types";
import { isMediaVideoType } from "../utils";

type UseHandleMediaChange = {
  selectedImages: PostStoryState["selectedImages"];
  setState: SetState;
  state: PostStoryState;
  onChange: PostStoryLocals["onChange"];
};

const isSharedArrayBufferAvailable = () => typeof SharedArrayBuffer !== "undefined";

const blobToFile = (blob: Blob, fileName: string) => new File(
  [blob], fileName, { type: blob.type }
);

const useHandleMediaChange = ({
  selectedImages, setState, state, onChange }: UseHandleMediaChange) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ffmpeg = isSharedArrayBufferAvailable() ? useFFmpeg() : null;
  const transcode = ffmpeg ? ffmpeg.transcode : async (media: any) => media;

  return async ({
    media,
    thumb,
    duration,
    finalVersion,
    isRecordedVideo = false
  }: HandleMediaChange) => {
    const isVideo = isMediaVideoType(media);

    setState({
      videoDuration: undefined,
      showUnsplashSearch: false,
      webVideoRecorded: isRecordedVideo
    });

    if (!media) return;

    if (isVideo) {
      if (duration) setState({ videoDuration: duration });
      if (thumb) setState({ selectedThumbnailUrl: URL.createObjectURL(thumb) });

      if (isRecordedVideo) {
        try {
          const transcodedVid = await transcode(media);

          if (transcodedVid) {
            const isSafari = () => {
              const agent = navigator.userAgent;
              return /^((?!chrome|android).)*safari/i.test(agent);
            };

            const hasSharedArrayBuffer = isSharedArrayBufferAvailable();
            const isSafariBrowser = isSafari();

            if (hasSharedArrayBuffer || isSafariBrowser) {
              setState({
                selectedMedia: transcodedVid,
                selectedImages: [],
                selectedVideos: [blobToFile(transcodedVid, "media")],
                selectedVideosChanged: true,
                mediaData: [],
                thumbnail: thumb
              });
            } else {
              setState({
                selectedMedia: media,
                selectedImages: [],
                selectedVideos: [blobToFile(media, "media")],
                selectedVideosChanged: true,
                mediaData: [],
                thumbnail: thumb
              });
            }
          } else {
            setState({
              selectedMedia: media,
              selectedImages: [],
              selectedVideos: [blobToFile(media, "media")],
              selectedVideosChanged: true,
              mediaData: [],
              thumbnail: thumb
            });
          }
        } catch (error) {
          errorService.error(`Error transcoding video: ${error}`);
          setState({
            selectedMedia: media,
            selectedImages: [],
            selectedVideos: [blobToFile(media, "media")],
            selectedVideosChanged: true,
            mediaData: [],
            thumbnail: thumb
          });
        }
      }
    } else {
      setState({ thumbnail: undefined, selectedThumbnailUrl: undefined, selectedMedia: media });
    }

    setState({ userSelectedImage: true });
    onChange();

    if (
      finalVersion
      && selectedImages.length < KEYS.MAX_NUMBER_IMAGES
      && !isVideo
    ) {
      setState({ selectedImages: [...selectedImages, media] });
    }
  };
};

export default useHandleMediaChange;
